// Pure open-circuit voltage-generation estimate for the PCB stator.
// No Angular / DI dependencies — math only, so it can be unit tested.
//
// A PM machine is reciprocal: Ke = Kt. For a coreless axial-flux machine that
// constant is set by Σ(rOuter² − rInner²)/2 over a phase's radial conductors —
// computed from the actual generated legs, so it is automatically shape-aware
// (the wedge's full-height legs make the sum larger → more voltage). Pole count
// sets the electrical frequency, not the amplitude, so it does not appear here.
// The conductor sum adds magnitudes (perfect alignment); the caller supplies
// the real winding factor kw (from the polarity planner) to correct for
// phasor misalignment in unbalanced configurations.
import { GerberData, WindingType } from './pcb-stator-geometry';

export interface VoltageEstimate {
  conductorSumMm2: number;    // Σ(rO²−rI²)/2 over one phase's radial legs, mm²
  kw: number;                 // winding factor actually applied (0 when degenerate)
  kePhaseVperKrpm: number;    // RMS phase volts per 1000 rpm (speed-independent)
  phaseRmsV: number;          // RMS phase voltage at the given rpm
  lineRmsV: number;           // RMS line-to-line at the given rpm
}

/** Σ(rOuter² − rInner²)/2 over the radial legs (arcIndex ≥ 0) of one phase, mm². */
export function phaseConductorSumMm2(
  data: GerberData,
  numPhases: number,
  phase: number,
): number {
  let sum = 0;
  for (const t of data.turns) {
    if (t.arcIndex < 0) continue;
    if (t.coilIndex % numPhases !== phase) continue;
    const rO = Math.hypot(t.outerX, t.outerY);
    const rI = Math.hypot(t.innerX, t.innerY);
    sum += Math.abs(rO * rO - rI * rI) / 2;   // magnitude: reverse layers store outer/inner directionally; series layers add EMF magnitudes
  }
  return sum;
}

export function computeVoltageGeneration(
  conductorSumMm2: number,
  airGapT: number,
  rpm: number,
  windingType: WindingType,
  kw: number,
): VoltageEstimate {
  const gM2 = conductorSumMm2 * 1e-6;

  if (airGapT <= 0 || rpm <= 0 || gM2 <= 0 || kw <= 0) {
    return { conductorSumMm2, kw: Math.max(kw, 0), kePhaseVperKrpm: 0, phaseRmsV: 0, lineRmsV: 0 };
  }

  const omega = (rpm * 2 * Math.PI) / 60;                 // mechanical rad/s
  const ePeak = airGapT * omega * gM2 * kw;               // peak phase volts
  const phaseRmsV = ePeak / Math.SQRT2;
  const kePhaseVperKrpm = (airGapT * ((1000 * 2 * Math.PI) / 60) * gM2 * kw) / Math.SQRT2;
  const lineRmsV = phaseRmsV * (windingType === 'wye' ? Math.sqrt(3) : 1);

  return { conductorSumMm2, kw, kePhaseVperKrpm, phaseRmsV, lineRmsV };
}
